import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import moment from 'moment-timezone';
import { CommonFunctionsService, DatastoreService } from '../../services';
import { isOurCeremonyFeatureEnabled } from '../../services/guards/feature-toggle.guard';

/** Whole days until the ceremony, Europe/London day boundaries. */
export function getDaysUntilCeremony(
  datetime: string | null | undefined
): number | null {
  if (!datetime) {
    return null;
  }
  return moment(datetime)
    .utc()
    .tz('Europe/London')
    .startOf('day')
    .diff(moment().tz('Europe/London').startOf('day'), 'days');
}

/**
 * Single source of truth for "can this booking be rescheduled": the
 * can_reschedule toggle is on AND the ceremony is 30+ days away. Drives the
 * our-ceremony Reschedule button, the cross-journey links on the cancel and
 * extend before-you-start pages, and rescheduleAllowedGuard — so a visible
 * link can never disagree with the guard and bounce the user to our-ceremony.
 */
export function isRescheduleAvailable(dataStore: DatastoreService): boolean {
  if (!isOurCeremonyFeatureEnabled(dataStore, 'can_reschedule')) {
    return false;
  }
  const days = getDaysUntilCeremony(dataStore.booking?.datetime);
  return days != null && days >= 30;
}

/**
 * Blocks the reschedule routes when the ceremony is within 30 days.
 * Mirrors the canReschedule check in our-ceremony.component, so users
 * can't bypass the hidden Reschedule button via URL bar / bookmark / back.
 *
 * If there's no booking in the datastore yet, defer to the per-component
 * redirect (every reschedule component already handles the missing-booking
 * case explicitly).
 */
export const rescheduleAllowedGuard: CanActivateFn = () => {
  const router = inject(Router);
  const dataStore = inject(DatastoreService);
  const commonFunctionsService = inject(CommonFunctionsService);

  const daysUntilCeremony = getDaysUntilCeremony(dataStore.booking?.datetime);
  if (daysUntilCeremony == null || daysUntilCeremony >= 30) {
    return true;
  }

  router.navigate([
    `${commonFunctionsService.getCompanyName()}/${commonFunctionsService.getCompanyId()}/our-ceremony`,
  ]);
  return false;
};
